import { IApiResponse } from "../../Orchestrator/Types/Index";

/**
 * Voucher API error codes that have a matching translation key, so they can be
 * written straight into voucherErrorAtom and rendered inline.
 *
 * Anything else must NOT be written through: apiRequest synthesises
 * `errorCode: "UNKNOWN_ERROR"` for any non-JSON or unrecognised error body
 * (see handleResponse), and that string has no bare translation key — react-intl
 * would render the literal "UNKNOWN_ERROR" to the customer.
 */
export const KNOWN_VOUCHER_ERROR_KEYS = [
    "paid_amount_exceeds_amount_to_pay",
    "cannot_use_voucher_twice_per_booking",
    "not_enough_on_voucher",
    "voucher_used",
    "voucher_not_found_from_booking",
    "voucher_not_found",
    "voucher_not_found_from_exchange_operation"
];

/**
 * Maps a voucher API error code to a translation key that is guaranteed to
 * resolve. `fallback` is what an unrecognised code degrades to, and differs by
 * caller: applying a bad code is most likely an invalid voucher, while a failed
 * removal says nothing about the voucher's validity.
 */
export function toVoucherErrorKey(errorCode?: string, fallback: string = "voucher_not_found"): string {
    return KNOWN_VOUCHER_ERROR_KEYS.includes(errorCode ?? "") ? errorCode! : fallback;
}

// Statuses that mean "the backend looked at this code and rejected it". Anything
// else (401/403 after a failed refresh, 429, 5xx, network) says nothing about
// the code, so it must not be reported as an invalid voucher.
const CODE_REJECTED_STATUSES = [400, 404, 409, 422];

/**
 * Maps a failed POST /customers/{id}/vouchers (My Account → Add voucher) to a
 * translation key, mirroring mobile's wording. INT answers an unknown code with
 * 404 `{error, status, message}`, which handleResponse can't parse, so
 * `errorCode` reads "UNKNOWN_ERROR". A backend code can therefore be in
 * `errorCode` (400 problem-details bodies) or only in `body` under `code` /
 * `cause.name` (where mobile reads it); all three are checked.
 */
export function toAddVoucherErrorKey(response: IApiResponse<unknown>): string {
    const body = (typeof response.body === "object" && response.body !== null ? response.body : {}) as {
        code?: unknown;
        cause?: { name?: unknown };
    };
    if ([response.errorCode, body.code, body.cause?.name].includes("not-unique")) return "voucherAlreadyInAccount";
    return CODE_REJECTED_STATUSES.includes(response.code) ? "invalidVoucherCodeCheckAndRetry" : "apiErrorMsg";
}
